<script setup>
import { computed, ref } from 'vue';
import { SMS_CONSENT_STATUS, SMS_CONSENT_CHIP_CONFIG, SMS_CONSENT_METHOD_LABELS } from '@/fhirworks/constants/smsConsent';

/**
 * Read-only 4-state SMS reminder consent chip (issue #4071, FR-5).
 *
 * Clicking opens a small popover with a per-status message. When `editable`, the popover also
 * offers an "Edit contact info" link that emits `edit` (#6392); otherwise it is informational.
 * Used on the mobile phone list item in the edit patient dialog and mirrored next to the patient
 * name in the appointment dialog.
 */
const props = defineProps({
    status: {
        type: String,
        default: SMS_CONSENT_STATUS.NOT_ASKED,
    },
    patientName: {
        type: String,
        default: 'This patient',
    },
    // ISO timestamp of the latest consent change (smsReminderConsent.lastUpdatedAt).
    date: {
        type: String,
        default: null,
    },
    // Capture method of the latest change (smsReminderConsent.method).
    method: {
        type: String,
        default: null,
    },
    // Clinic's dedicated SMS number, shown in the carrier opt-out message when known.
    clinicNumber: {
        type: String,
        default: null,
    },
    // Show the "Edit contact info" link; the parent opens the edit dialog on `edit`.
    editable: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['edit']);

const menuOpen = ref(false);

const updateHint = computed(() => (props.editable ? '' : " Update in the patient's demographics."));

function onEdit() {
    menuOpen.value = false;
    emit('edit');
}

const config = computed(() => SMS_CONSENT_CHIP_CONFIG[props.status] || SMS_CONSENT_CHIP_CONFIG[SMS_CONSENT_STATUS.NOT_ASKED]);

const formattedDate = computed(() => {
    if (!props.date) return '';
    const parsed = new Date(props.date);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
});

const message = computed(() => {
    const who = props.patientName;
    const when = formattedDate.value;
    const methodLabel = props.method ? SMS_CONSENT_METHOD_LABELS[props.method] || props.method : '';
    const via = methodLabel ? ` via ${methodLabel}` : '';

    switch (props.status) {
        case SMS_CONSENT_STATUS.OPTED_IN:
            return `${who} opted in${when ? ` on ${when}` : ''}${via}.`;
        case SMS_CONSENT_STATUS.DECLINED:
            return `${who} declined${when ? ` on ${when}` : ''}.${updateHint.value}`;
        case SMS_CONSENT_STATUS.OPTED_OUT_CARRIER: {
            const resume = props.clinicNumber ? ` To resume, they must text START to ${props.clinicNumber} from their phone.` : ` To resume, they must text START from their phone.`;
            return `${who} opted out${when ? ` on ${when}` : ''}.${resume}`;
        }
        case SMS_CONSENT_STATUS.NOT_ASKED:
        default:
            return `SMS reminder consent has not been captured.${updateHint.value}`;
    }
});
</script>

<template>
    <v-menu v-model="menuOpen" location="bottom start" :close-on-content-click="false">
        <template #activator="{ props: menuProps }">
            <v-chip v-bind="menuProps" label link :color="config.color" role="button">
                <font-awesome-icon :icon="['far', config.icon]" class="mr-1" />
                {{ config.label }}
            </v-chip>
        </template>
        <v-card max-width="280">
            <v-card-text class="text-body-2">{{ message }}</v-card-text>
            <v-card-actions v-if="editable">
                <v-btn variant="text" size="small" color="primary" data-cy="smsConsentChipEditLink" @click="onEdit">Edit contact info</v-btn>
            </v-card-actions>
        </v-card>
    </v-menu>
</template>
